import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject,
         OnDestroy
       } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { ImageQualityRatingService
       } from 'app/controls/image-quality-rating/image-quality-rating.service';
import { ImageQualityDialogComponent
       } from 'app/dialogs/image-quality-dialog/image-quality-dialog.component';
import { ImagePlaceholder
       } from 'collage-designer/stage/placeholder/image-placeholder';
import { Placeholder } from 'collage-designer/stage/placeholder/placeholder';
import { PlaceholderService
       } from 'collage-designer/stage/placeholder/placeholder.service';
import { TranslationService } from 'core/translation/translation.service';
import { Utils } from 'core/utils';
import { Subscription } from 'rxjs';

@Component({
    selector: 'image-quality-rating',
    templateUrl: './image-quality-rating.component.html',
    styleUrls: ['./image-quality-rating.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class ImageQualityRatingComponent implements OnDestroy
{
  public imageQualityRating: string = 'good';
  public qualityRatingValue: number | undefined;
  public badImagesCount: number = 0;
  public totalImageCount: number = 0;
  public visible: boolean = false;
  public qualityIndicatorsVisible: boolean = false;
  private subscriptions: Subscription = new Subscription();

  constructor(
      private dialog: MatDialog,
      private cdr: ChangeDetectorRef,
      @Inject(TranslationService) private translationService,
      @Inject(PlaceholderService) private readonly placeholderService:
          PlaceholderService,
      @Inject(ImageQualityRatingService) private imageQualityRatingService:
          ImageQualityRatingService)
  {
    this.subscriptions.add(
        this.placeholderService.focusedPlaceholderChanged.subscribe(
          () => this.updateQualityRating()));

    this.subscriptions.add(
        this.placeholderService.placeholderChanged.subscribe(
          (placeholder) => {
            if (placeholder instanceof ImagePlaceholder &&
                this.placeholderService.areQualityIndicatorsVisible()) {
              placeholder.showQualityIndication();
            }
            this.updateQualityRating();
          }));

    this.subscriptions.add(
        this.placeholderService.imageBeingEdited.subscribe(
          (placeholder) => {
            this.imageQualityRatingService.readQualityRating(placeholder);
            this.updateQualityRating();
          }));
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
  }

  private updateQualityRating(): void
  {
    const placeholders = this.placeholderService.getImagePlaceholders();
    const focused = this.placeholderService.getFocusedPlaceholder();

    if (focused instanceof ImagePlaceholder &&
        focused.hasEditableContent()) {
      this.rateQualitySinglePlaceholder(focused);
    } else if (placeholders.length === 1) {
      this.rateQualitySinglePlaceholder(placeholders[0]);
    } else {
      this.rateQualityAllPlaceholders(placeholders);
    }

    this.qualityIndicatorsVisible =
        this.placeholderService.areQualityIndicatorsVisible();
    this.rate();
    this.cdr.markForCheck();
  }

  private rateQualitySinglePlaceholder(placeholder: ImagePlaceholder): void
  {
    this.qualityRatingValue = placeholder.getQualityRating();
    this.badImagesCount = 0;
    this.totalImageCount = 1;
    this.visible = placeholder.hasEditableContent() &&
        !placeholder.isPending() &&
        this.qualityRatingValue !== undefined;
  }

  private rateQualityAllPlaceholders(
      placeholders: Array<ImagePlaceholder>): void
  {
    const qualityRatings = placeholders
      .filter((placeholder) =>
          placeholder.hasEditableContent() && !placeholder.isPending())
      .map((placeholder) => placeholder.getQualityRating())
      .filter((qr) => qr !== undefined);

    this.qualityRatingValue = qualityRatings.length === 0 ?
        undefined : Math.min.apply(Math, qualityRatings);

    this.badImagesCount =
        qualityRatings.filter((qr) => qr <= 3).length;

    this.totalImageCount = qualityRatings.length;
    this.visible = this.totalImageCount > 0;
  }

  private rate(): void
  {
    if (this.qualityRatingValue > 3) {
      this.imageQualityRating = 'good';
    }
    else if (this.qualityRatingValue === 3) {
      this.imageQualityRating = 'medium';
    }
    else {
      this.imageQualityRating = 'bad';
    }
  }

  public get messageTitle(): string
  {
    return this.translationService.get(
        'i18n.control.imageQuality.title.' + this.imageQualityRating);
  }

  public get messageContent(): string
  {
    return this.translationService.get(
        'i18n.control.imageQuality.content.' + this.imageQualityRating);
  }

  public get badImagesCounterLabel(): string
  {
    return this.translationService.get(
        'i18n.control.imageQuality.affectedImages', this.badImagesCount);
  }

  public toggleQualityIndicators(): void
  {
    this.placeholderService.showQualityIndicators(
        !this.qualityIndicatorsVisible);
    this.qualityIndicatorsVisible =
        this.placeholderService.areQualityIndicatorsVisible();
  }

  public openImageQualityInfoOverlay(): void
  {
    this.dialog
      .open(
          ImageQualityDialogComponent,
          {
           data: {imageQualityRating: this.imageQualityRating},
           panelClass: 'dialog-pane'
          });
  }
}
